import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {Text, View} from "@khanacademy/wonder-blocks-core";
import {doNotTranslate} from "@khan/i18n";
import {mediaQueries} from "@khan/shared-styles";
import {staticUrl} from "@khan/urls";

import {MarketingColors} from "#marketing-content-shared-package/constants.ts";
import Illustration from "#marketing-content-shared-package/illustration.tsx";

type Props = {
    backgroundIllustration?: string;
};

export default class Supporters extends React.Component<Props> {
    render(): React.ReactNode {
        const {backgroundIllustration} = this.props;

        const supporters = [
            {
                name: doNotTranslate`Bank of America`,
                src: "/images/supporters-logos/bank-of-america-dark-gray@2x.png",
                styles: styles.bankOfAmerica,
            },
            {
                name: doNotTranslate`College Board`,
                src: "/images/supporters-logos/college-board-dark-gray@2x.png",
                styles: styles.collegeBoard,
            },
            {
                name: doNotTranslate`Ann and John Doerr`,
                src: "/images/supporters-logos/ann-and-john-doerr-dark-gray@2x.png",
                styles: styles.annAndJohnDoerr,
            },
            {
                name: doNotTranslate`Bill and Melinda Gates Foundation`,
                src: "/images/supporters-logos/gates-foundation-dark-gray@2x.png",
                styles: styles.gatesFoundation,
            },
            {
                name: doNotTranslate`Fundação Lemann`,
                src: "/images/supporters-logos/lemann-foundation-dark-gray@2x.png",
                styles: styles.lemannFoundation,
            },
            {
                name: doNotTranslate`Carlos Rodriguez-Pastor`,
                src: "/images/supporters-logos/carlos-rodriguez-pastor-dark-gray@2x.png",
                styles: styles.carlosRodriguezPastor,
            },
            {
                name: doNotTranslate`Tata Trusts`,
                src: "/images/supporters-logos/tata-trusts-dark-gray@2x.png",
                styles: styles.tataTrusts,
            },
            {
                name: doNotTranslate`Valhalla Charitable Foundation`,
                src: "/images/supporters-logos/valhalla-dark-gray@2x.png",
                styles: styles.valhalla,
            },
        ];

        return (
            <View style={styles.supporters}>
                {backgroundIllustration && (
                    <Illustration name={backgroundIllustration} />
                )}
                <View style={styles.container}>
                    <Text tag="h2" style={styles.header}>
                        {t`Key supporters`}
                    </Text>
                    <View style={styles.list}>
                        {supporters.map((supporter, i) => (
                            <View key={i} style={styles.listItem}>
                                <img
                                    className={css(
                                        styles.supporter,
                                        supporter.styles,
                                    )}
                                    alt={supporter.name}
                                    src={staticUrl(supporter.src)}
                                />
                            </View>
                        ))}
                    </View>
                </View>
            </View>
        );
    }
}

const styles = StyleSheet.create({
    supporters: {
        overflow: "hidden",
    },
    // The header styles went away from being one of our existing marketing
    // font styles. Since this is the only place this heading style is used,
    // I'm creating it as a one-off and not as a reusable component.
    header: {
        color: MarketingColors.darkGray,
        display: "block",
        fontFamily: "'Source Serif Pro', 'Noto Serif', serif",
        fontSize: 32,
        fontWeight: 600,
        lineHeight: "48px",

        [mediaQueries.mdOrSmaller]: {
            fontSize: 22,
            lineHeight: "30px",
            textAlign: "center",
        },
    },
    container: {
        padding: "115px 64px 78px 64px",
        width: "100%",
        //background: MarketingColors.lightTan,
        alignItems: "center",
        [mediaQueries.mdOrSmaller]: {
            paddingTop: 109,
            paddingBottom: 42,
            paddingLeft: 80,
            paddingRight: 80,
        },
        [mediaQueries.smOrSmaller]: {
            paddingTop: 90,
            paddingBottom: 22,
            paddingLeft: 40,
            paddingRight: 40,
        },
    },
    list: {
        paddingTop: 68,
        maxWidth: 1024,
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "center",
        alignItems: "center",
        [mediaQueries.lgOrSmaller]: {
            maxWidth: 540,
        },
        [mediaQueries.smOrSmaller]: {
            width: "auto",
            flexDirection: "column",
            marginLeft: "auto",
            marginRight: "auto",
            paddingTop: 58,
        },
    },
    listItem: {
        width: "auto",
        alignItems: "center",
        flexShrink: 0,
        textAlign: "left",
        margin: "0 28px 32px",
        justifyContent: "center",
        [mediaQueries.lgOrSmaller]: {
            flexBasis: "50%",
            margin: "0 0 68px 0",
            alignItems: "flex-start",

            // @ts-expect-error - TS2418 - Type of computed property's value is '{ flexBasis: string; margin: string; alignItems: "flex-start"; ":nth-child(even)": { alignItems: string; }; }', which is not assignable to type 'Properties<string | number, string & {}>'.
            ":nth-child(even)": {
                alignItems: "flex-end",
            },
        },
        [mediaQueries.smOrSmaller]: {
            width: "100%",
            margin: "0 0 58px 0",
            flexBasis: "auto",
            alignItems: "center",

            // @ts-expect-error - TS2418 - Type of computed property's value is '{ width: string; margin: string; flexBasis: string; alignItems: "center"; ":nth-child(even)": { alignItems: string; }; }', which is not assignable to type 'Properties<string | number, string & {}>'.
            ":nth-child(even)": {
                alignItems: "center",
            },
        },
    },
    // A generic reset for the pixel pushing of individual supporter logos
    supporter: {
        [mediaQueries.mdOrSmaller]: {
            margin: 0,
        },
    },
    gatesFoundation: {
        width: 215.5,
        height: 43,
        [mediaQueries.smOrSmaller]: {
            width: 174,
            height: 35,
        },
    },
    annAndJohnDoerr: {
        width: 165.5,
        height: 19,
        [mediaQueries.smOrSmaller]: {
            width: 144,
            height: 16,
        },
    },
    bankOfAmerica: {
        width: 204,
        height: 21.2,
        margin: "-11px 0 0 -11px",
        [mediaQueries.smOrSmaller]: {
            width: 165,
            height: 17,
        },
    },
    valhalla: {
        width: 155,
        height: 32,
        margin: "16px 0 0 0",
        [mediaQueries.smOrSmaller]: {
            width: 134,
            height: 27.5,
        },
    },
    tataTrusts: {
        width: 172.5,
        height: 17,
        margin: "8px 0 0 0",
        [mediaQueries.smOrSmaller]: {
            width: 139,
            height: 14,
        },
    },
    lemannFoundation: {
        width: 151,
        height: 48,
        [mediaQueries.smOrSmaller]: {
            width: 122,
            height: 39,
        },
    },
    carlosRodriguezPastor: {
        width: 221,
        height: 20,
        margin: "16px 0 0 0",
        [mediaQueries.smOrSmaller]: {
            width: 192,
            height: 17.5,
        },
    },
    collegeBoard: {
        width: 189,
        height: 32.5,
        margin: "-11px 0 0 0",
        [mediaQueries.smOrSmaller]: {
            width: 152.5,
            height: 26,
        },
    },
});
